import PopupState from 'material-ui-popup-state';
import cc from 'classcat';
import React, { useMemo, useState } from 'react';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { bindMenu, bindTrigger } from 'material-ui-popup-state/hooks';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import C from './GroupFormModal.css';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ListItemIcon from '@mui/material/ListItemIcon';
import { useCommunityDetails } from 'atoms/community-details';
import GroupFormModal from './GroupFormModal';
import { msg, t, Trans } from '@lingui/macro';
import { Stack } from '@mui/material';
import { ModeEdit, Delete, Cancel } from '@mui/icons-material';
import { useLingui } from '@lingui/react';
import swal from 'sweetalert2';
import { httpClient } from '@cocoplatform/coco-rtc-client';
import { reportServerError } from 'utils/report-error';
import { useUserProfile } from 'atoms/user-profile';

export default function GroupMenuTrigger(p: {
  style?: React.CSSProperties;
  communityId: string;
  groupId: string;
  className?: string;
  selectedGroupMembers: {
    user: { id: string };
    email?: string;
  }[];
}) {
  const { community, deleteGroup, loadCommunityDetails, removeMember } =
    useCommunityDetails({
      communityId: p.communityId,
      autoLoad: false,
    });

  const { userProfile } = useUserProfile();

  const group = useMemo(() => {
    return community?.groups?.find((g) => g.id === p.groupId);
  }, [p.groupId]);

  const connectedClassrooms = community?.googleClassrooms;

  const isGroupConnected = connectedClassrooms?.find(
    (c) => c.id === group?.googleClassroom?.id,
  );

  const [isUpdating, setUpdating] = useState(false);

  const { _ } = useLingui();

  return (
    <>
      {isUpdating && (
        <GroupFormModal
          communityId={p.communityId}
          group={group}
          onClose={() => {
            setUpdating(false);
          }}
        />
      )}
      <PopupState variant='popover' popupId='group-edit-btn'>
        {(popupState) => {
          return (
            <Stack direction='row' spacing={1}>
              <Button
                sx={{
                  borderRadius: '20px',
                  fontSize: 'var(--font-size-subtext) !important',
                  marginLeft: '1rem',
                }}
                {...bindTrigger(popupState)}
                startIcon={
                  <ModeEdit
                    sx={{
                      width: 'var(--font-size-subtext)',
                      marginRight: '0',
                    }}
                  />
                }
                size='small'
                variant='outlined'
              >
                <Trans>Edit</Trans>
              </Button>
              <Menu {...bindMenu(popupState)}>
                <MenuItem
                  onClick={() => {
                    setUpdating(true);
                    popupState.close();
                  }}
                >
                  <ListItemIcon>
                    <EditIcon fontSize='small' />
                  </ListItemIcon>
                  <ListItemText>Edit</ListItemText>
                </MenuItem>
                {isGroupConnected && (
                  <MenuItem
                    onClick={async () => {
                      popupState.close();
                      const { isConfirmed } = await swal.fire({
                        title: _(
                          'If you disconnect this classroom, all students/teachers from the classroom (except you) will be removed from this community and will no longer have access. Are you sure you want to disconnect?',
                        ),
                        icon: 'warning',
                        showCancelButton: true,
                        confirmButtonText: _('Disconnect'),
                        cancelButtonText: _('Cancel'),
                      });

                      if (!isConfirmed) {
                        return;
                      }

                      await httpClient.delete(
                        `/community/${p.communityId}/classroom-invitations`,
                        {
                          data: {
                            classroomId: group?.googleClassroom?.id,
                          },
                        },
                      );

                      await bulkDeleteUsers({
                        usersToDelete: p.selectedGroupMembers
                          .map((m) => {
                            if (m.user) {
                              return { userId: m.user.id };
                            }

                            return { email: m.email };
                          })
                          .filter(
                            (m) =>
                              m.userId !== userProfile?.id &&
                              m.email !== userProfile?.email,
                          ),
                      });

                      deleteGroup(p.groupId);

                      await loadCommunityDetails();
                    }}
                  >
                    <ListItemIcon>
                      <Cancel fontSize='small' />
                    </ListItemIcon>
                    <ListItemText>Disconnect Google Classroom</ListItemText>
                  </MenuItem>
                )}
                {!isGroupConnected && (
                  <MenuItem
                    onClick={async () => {
                      const { isConfirmed } = await swal.fire({
                        title: _('Are you sure you want to delete this group?'),
                        icon: 'warning',
                        showCancelButton: true,
                        confirmButtonText: _('Delete'),
                        cancelButtonText: _('Cancel'),
                      });

                      if (!isConfirmed) {
                        return;
                      }
                      deleteGroup(p.groupId);
                      popupState.close();
                    }}
                  >
                    <ListItemIcon>
                      <DeleteOutlineIcon fontSize='small' />
                    </ListItemIcon>
                    <ListItemText>Delete</ListItemText>
                  </MenuItem>
                )}
              </Menu>
            </Stack>
          );
        }}
      </PopupState>
    </>
  );

  async function bulkDeleteUsers({
    usersToDelete,
  }: {
    usersToDelete: {
      userId?: string;
      email?: string;
    }[];
  }) {
    if (usersToDelete.length === 0) return;

    try {
      await httpClient.post(`/community/${p?.communityId}/bulk-delete-users`, {
        users: usersToDelete,
      });
    } catch (error) {
      reportServerError({
        error,
        title: _(msg`Failed to delete users`),
      });
      return;
    }

    removeMember(usersToDelete);
  }
}
